/* import __COLOCATED_TEMPLATE__ from './mcp-actions-collapsible-list.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import type ThemeService from 'embercom/services/theme-service';
import {
  getMcpTemplateByName,
  getListIconForMcpServer,
} from 'embercom/models/workflow-connector/constants/mcp-templates';
import type McpServer from 'embercom/models/workflow-connector/mcp-server';

const DEFAULT_EXPANDED_LIMIT = 5;

interface Args {
  title: string | null;
  target: string;
  actions: any[];
  selectedColumns: any[];
  onAddActionClick: () => void;
  mcpServer?: McpServer;
  showEditOrDeleteMcpServerModal?: (mcpServer: McpServer) => void;
  isDefaultDataConnector?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
}

export default class McpActionsCollapsibleList extends Component<Args> {
  @service declare intl: IntlService;
  @service declare themeService: ThemeService;

  @tracked localIsOpen = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.localIsOpen =
      this.args.actions.length > 0 && this.args.actions.length <= DEFAULT_EXPANDED_LIMIT;
  }

  get isOpen() {
    return this.args.isOpen !== undefined ? this.args.isOpen : this.localIsOpen;
  }

  get arrowIconClass() {
    return this.isOpen ? 'small-up-arrow' : 'small-down-arrow';
  }

  get mcpServerIcon(): string {
    if (this.args.isDefaultDataConnector) {
      return 'mcp/default';
    }

    return getListIconForMcpServer(
      this.args.mcpServer?.name ?? '',
      this.themeService.darkModeEnabled,
    );
  }

  get mcpServerDescription(): string | null {
    let descriptionKey = this.getMcpServerDescriptionKey();
    return descriptionKey ? this.intl.t(descriptionKey) : null;
  }

  private getMcpServerDescriptionKey(): string | null {
    if (this.args.mcpServer?.name === 'default_connectors') {
      return 'workflow-connector.actions.data-connectors-description';
    }

    if (this.args.mcpServer?.name) {
      let template = getMcpTemplateByName(this.args.mcpServer.name);
      if (template) {
        return template.descriptionKey;
      }
    }

    return null;
  }

  @action
  toggleCollapse() {
    if (this.args.onToggle) {
      this.args.onToggle();
    } else {
      this.localIsOpen = !this.localIsOpen;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'WorkflowConnector::McpActionsCollapsibleList': typeof McpActionsCollapsibleList;
    'workflow-connector/mcp-actions-collapsible-list': typeof McpActionsCollapsibleList;
  }
}
